// Angular
import { Component, ComponentFactoryResolver, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
// Service
import { BroadcastService, ModalService, ModalSize, ObjectUtils } from '@cv-invision/core';
import { ScrollableItemsHelperOsService } from '../../../../services/scrollable-items-helper-os.service';
import { ViewsService } from '../../../../../services/views.service';
import { LayoutRenderService } from '../../../services/layout-render.service';
// Model
import { ArraySectionDirectiveModel } from '../../../../models/extended-section-options';
import { DisposableInterface } from '../../../../models/disposable.interface';
import { CaptureType } from './model/report-image.model';
import { ReportImage } from './model/panorama-tour.model';
import { PanoramaEventType, RMCEventType } from '../../models/component.events';
import { PanoramaTourOptions } from '../../models/component-options/panorama-tour.options';
import { LayoutComponentBroadcast, LayoutRenderLayoutItem } from '../../../models/cv-layout-render.model';
import { ComponentList } from '../../../models/components.enum';
import { FormModelType, ResourceMediaModel } from '../resource-media-control/model/resource-media.model';
// Component
import { ReportImageComponent } from '../resource-media-control/report-image/report-image.component';
import { DrawFormsComponent } from "../draw-forms/draw-forms.component";
// Other
import * as THREE from 'three';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FORM_EVENT } from '@cv-invision/cv.invision-views.ux';

@Component({
	selector: 'cv-schema-render-panorama-tour',
	templateUrl: './panorama-tour.component.html'
})
export class PanoramaTourComponent implements OnInit, OnDestroy, DisposableInterface {
	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();
	@ViewChild('viewReportImage', { static: false }) viewReportImage: ReportImageComponent;

	options = new PanoramaTourOptions();
	thisData: any;
	photos = [];
	public metaData: any;
	public isDisposed: boolean = null;

	private atComponentDestroy = new Subject();
	public directiveData = new ArraySectionDirectiveModel();

	public reportImage: ReportImage = new ReportImage();

	public album: any = [];
	public currentAlbumPanorama;
	public currentImageTexture = null;
	// Render
	public sceneRender = {
		renderer: null,
		mesh: null,
		scene: null,
		camera: null,
		geometry: null,
		texture: null
	};
	public currentAnimation: any = null;
	public isUserInteracting = false;
	public onPointerDownMouseX = 0;
	public onPointerDownMouseY = 0;
	public lon = 0;
	public onPointerDownLon = 0;
	public lat = 0;
	public onPointerDownLat = 0;
	public phi = 0;
	public theta = 0;
	public sceneContainer;
	public sceneLoaderContainer;
	public geometry;
	public sceneWidthArea = 0;
	public sceneHeightArea = 0;
	public povPosition = {
		x: 0,
		y: 0,
		z: 0,
		fov: 75
	};
	// Capture Type
	public captureType = CaptureType;
	// FakeSceneRender
	public fakeSceneRender = {
		renderer: null,
		mesh: null,
		scene: null,
		camera: null
	};

	/**
	 * Get the Title
	 */
	get title(): string {
		let title = '';
		if ('readDescriptionFromData' in this.options) {
			if (Array.isArray(this.options.readDescriptionFromData.key)) {
				const dataPieces = [];
				this.options.readDescriptionFromData.key.forEach(datapointKey => {
					dataPieces.push(this.thisData[datapointKey]);
				});
				title = dataPieces.join(this.options.readDescriptionFromData.separator || ' | ');
			} else {
				title = this.thisData[this.options.readDescriptionFromData.key] || '';
			}
		}

		return title;
	}

	/**
	 * Get the Current Index the Gallery is at
	 */
	get currentIndexAlbumSelected(): any {
		return (this.directiveData.component &&
			this.directiveData.component.metaData &&
			this.directiveData.component.metaData.currentIndexAlbum) ?
			this.directiveData.component.metaData.currentIndexAlbum : 0;
	}

	// Photo Gallery Component -------------------------------------------------------------------
	get photosSelected(): any {
		return this.viewsService.photosSelected;
	}

	get isEditable(): boolean {
		return this.layoutRenderService.extraData.isEditing;
	}

	constructor(
		private layoutRenderService: LayoutRenderService,
		private elementRef: ElementRef,
		private viewsService: ViewsService,
		private broadcastService: BroadcastService,
		private scrollableItemsHelperOsService: ScrollableItemsHelperOsService,
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver
	) {
	}

	ngOnInit(): void {
		this.options = { ...this.options, ...this.layoutNode.options };
		if (this.options.photosInData) {
			this.photos = this.data.photos;
			this.thisData = this.data.data;
		} else {
			// calculate photos given the path in data
			this.thisData = this.data.data;
		}

		this.retain();
		// Get the eet of images from the Path
		const images = this.photos || [];

		if (images && images.length) {
			images.forEach(img => {
				this.album.push(img);
			});
			// Create Panorama Tour
			this.initializePanoramaTour();
		}

		this.layoutRenderService.componentBroadcast
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((layoutComponentBroadcast: LayoutComponentBroadcast) => {
				if (
					layoutComponentBroadcast.eventType === PanoramaEventType.SAVED_SNAPSHOT &&
					layoutComponentBroadcast.jsonPath === this.thisData._Path
				) {
					this.clearSnapshot();
				}
			});
	}

	/**
	 * After a Save or after Cancel the Snapshot
	 */
	public clearSnapshot(): void {
		this.reportImage.disabledControls = false
		this.reportImage.snapshot = null;
		this.reportImage.captureType = null;
		this.disposeFakeSceneRenderer();
	}

	/**
	 * Re-acquire the Component after was disposed
	 */
	public retain(): void {

		const indexes = this.thisData._Data.index;
		const section = this.thisData._Data.section;
		const sectionId = `${section}-${indexes.join('-')}`;

		const currentDirective = this.scrollableItemsHelperOsService.getDisposableItem(section, sectionId);

		if (currentDirective) {
			this.directiveData = currentDirective;
			this.directiveData.component.retain = this.regenerateComponent.bind(this);
			this.directiveData.component.dispose = this.dispose.bind(this);
		} else {
			this.directiveData.skipDirective = false;
			this.directiveData.visible = true;
			this.directiveData.rendered = true;
			this.directiveData.component = {
				retain: this.regenerateComponent.bind(this),
				dispose: this.dispose.bind(this),
				metaData: {
					currentIndexAlbum: 0
				}
			};
		}
		// Just in case it changes
		this.directiveData.indexes = indexes;
		this.directiveData.section = section;
		this.directiveData.cssClass = sectionId;
	}

	/**
	 * When the script is loaded and ready to use
	 */
	public initializePanoramaTour = (): void => {
		this.sceneHeightArea = 490;
		this.sceneWidthArea = 653;
		// We get the element as a class, outside Angular to keep outside the Zone
		this.sceneContainer = this.elementRef.nativeElement.querySelector('.photo-container .render-content');
		this.sceneLoaderContainer = this.elementRef.nativeElement.querySelector('.photo-container .render-loader');
		const panoramaTourWrapper = this.elementRef.nativeElement.querySelector('.photo-gallery-wrapper');

		// It keeps the mouse enter/leave to being able to execute Three evens only on the element
		panoramaTourWrapper.addEventListener('mouseenter', e => {
			this.isUserInteracting = true;
			this.disableScroll();
		});
		panoramaTourWrapper.addEventListener('mouseleave', e => {
			this.isUserInteracting = false;
			this.enableScroll();
		});

		this.createNewInstance();
	}

	/**
	 * Regenerate the Component
	 */
	public regenerateComponent = (): void => {
		if (this.isComponentAlreadyDispose()) {
			this.createNewInstance();
		}
	}

	/**
	 * Create a ThreeJs instance
	 */
	public createNewInstance = (): void => {
		this.isDisposed = null;
		// Define how to get the proper width/height
		this.sceneRender.camera = new THREE.PerspectiveCamera(75, this.sceneWidthArea / this.sceneHeightArea, 1, 1100);
		// Define Geometry to create the Sphere (Panorama) effect
		this.geometry = new THREE.SphereGeometry(500, 60, 40);
		// invert the geometry on the x-axis so that all of the faces point inward
		this.geometry.scale(-1, 1, 1);
		// Load if exists previous selected image
		this.directiveData.component.metaData = {
			currentIndexAlbum: this.currentIndexAlbumSelected
		};
		this.currentAlbumPanorama = this.album[this.directiveData.component.metaData.currentIndexAlbum];

		this.loadTexture();
	}

	/**
	 * Mark the Component as Dispose
	 */
	public dispose(): void {
		if (!this.isComponentAlreadyDispose()) {
			cancelAnimationFrame(this.currentAnimation);

			if (this.sceneRender.scene !== null) {
				for (let i = this.sceneRender.scene.children.length - 1; i >= 0; i--) {
					const object = this.sceneRender.scene.children[i];
					if (object.type === 'Mesh') {
						object.geometry.dispose();
						object.material.dispose();
						this.sceneRender.scene.remove(object);
					}
				}
				this.sceneRender.scene = null;
			}

			if (this.sceneRender.mesh !== null && this.sceneRender.mesh.material) {
				this.sceneRender.mesh.material.dispose();
			}
			this.sceneRender.mesh = null;
			this.sceneRender.camera = null;

			if (this.sceneRender.texture !== null) {
				this.sceneRender.texture.dispose();
				this.sceneRender.texture = null;
			}

			if (this.sceneRender.geometry !== null) {
				this.sceneRender.geometry.dispose();
				this.sceneRender.geometry = null;
			}

			if (this.sceneRender.renderer !== null) {
				this.sceneRender.renderer.dispose();
				this.sceneRender.renderer = null;
			}

			if (this.sceneContainer) {
				this.sceneLoaderContainer.style.display = 'block';
				this.sceneContainer.innerHTML = '';
			}

			this.disposeFakeSceneRenderer();

			this.isDisposed = true;
		}
	}

	/**
	 * Dispose the Current Fake Scene Renderer
	 * @private
	 */
	public disposeFakeSceneRenderer(): void {
		if (this.fakeSceneRender.mesh !== null && this.fakeSceneRender.mesh.materia) {
			this.fakeSceneRender.mesh.material.dispose();
		}
		this.fakeSceneRender.mesh = null;
		this.fakeSceneRender.scene = null;
		this.fakeSceneRender.camera = null;
		if (this.fakeSceneRender.renderer !== null) {
			this.fakeSceneRender.renderer.dispose();
		}
		this.fakeSceneRender.renderer = null;
	}

	/**
	 * Component has been dispose
	 */
	public isComponentAlreadyDispose(): boolean {
		return this.isDisposed !== null || (this.isDisposed === false &&
			this.sceneRender.scene !== null && this.sceneRender.geometry !== null && this.sceneRender.camera !== null);
	}

	/**
	 * Load the new Texture based on the Current Album Panorama
	 */
	public loadTexture(): void {
		// Remove previous Texture if exists
		if (this.sceneRender.texture !== null) {
			this.sceneRender.texture.dispose();
		}
		this.sceneRender.texture = new THREE.TextureLoader().load(
			this.currentAlbumPanorama.path,
			(texture: any) => {
				this.currentImageTexture = texture;
				this.createScene(texture);
			});
	}

	/**
	 * Set Previous Panorama Picture
	 */
	public previousPanorama(): void {
		if (this.reportImage.disabledControls === false) {

			// We reach first element, go back to last one
			if (this.directiveData.component.metaData.currentIndexAlbum === 0) {
				this.directiveData.component.metaData.currentIndexAlbum = this.album.length - 1;
			} else {
				this.directiveData.component.metaData.currentIndexAlbum--;
			}

			this.currentAlbumPanorama = this.album[this.directiveData.component.metaData.currentIndexAlbum];
			this.loadTexture();
		}
	}

	/**
	 * Set Next Panorama Picture
	 */
	public nextPanorama(): void {
		if (this.reportImage.disabledControls === false) {
			this.directiveData.component.metaData.currentIndexAlbum++;
			// We reach last element, go back to 0
			if (this.album.length === this.directiveData.component.metaData.currentIndexAlbum) {
				this.directiveData.component.metaData.currentIndexAlbum = 0;
			}
			this.currentAlbumPanorama = this.album[this.directiveData.component.metaData.currentIndexAlbum];
			this.loadTexture();
		}
	}

	/**
	 * Create a new Scene Element
	 */
	public createScene(texture: any): void {
		// New Scene for the Panorama
		this.sceneRender.scene = new THREE.Scene();
		// Clear previous Scene
		this.sceneContainer.innerHTML = '';
		this.onImageLoaded(texture);
	}

	/**
	 * After the image has been loaded we can attach all the events
	 * @param texture
	 * @param container
	 * @param geometry
	 */
	public onImageLoaded(texture: any): void {
		// On change the Image, put back the POV from the lat and lon
		this.lat = 0;
		this.lon = 0;

		const material = new THREE.MeshBasicMaterial({ map: texture });

		this.sceneRender.mesh = new THREE.Mesh(this.geometry, material);

		this.sceneRender.scene.add(this.sceneRender.mesh);

		this.sceneRender.renderer = new THREE.WebGLRenderer({ preserveDrawingBuffer: true });
		this.sceneRender.renderer.setPixelRatio(1);
		this.sceneRender.renderer.setSize(this.sceneWidthArea, this.sceneHeightArea, false);
		// Add new Fresh Scene
		this.sceneLoaderContainer.style.display = 'none';
		this.sceneContainer.appendChild(this.sceneRender.renderer.domElement);

		this.sceneContainer.style.touchAction = 'none';

		// It attach the Pointer Down Event
		this.sceneContainer.addEventListener('pointerdown', this.onPointerDown);

		document.addEventListener('wheel', this.onMouseWheel);

		// It attach the Drag Over Event
		document.addEventListener('dragover', this.onDragOver);
		// We Execute the animation
		this.animate();
	}

	/**
	 * On Drag Over Prevent Default Event
	 * @param event
	 */
	public onDragOver = (event: any): void => {
		event.preventDefault();
	}

	/**
	 * On Mouse Wheel Zoom
	 * @param event
	 */
	public onMouseWheel = (event: any): void => {
		if (this.isUserInteracting && this.reportImage.disabledControls === false && !this.isComponentAlreadyDispose()) {
			this.povPosition.fov = this.sceneRender.camera.fov + event.deltaY * 0.05;
			// Min is ZoomIn TODO: review preferred value
			this.sceneRender.camera.fov = THREE.MathUtils.clamp(this.povPosition.fov, 40, 95);
			this.sceneRender.camera.updateProjectionMatrix();
		}
	}

	/**
	 * It attach the Pointer Down Event
	 * @param event
	 */
	public onPointerDown = (event: any): void => {
		if (this.reportImage.disabledControls === false && !this.isComponentAlreadyDispose()) {
			if (event.isPrimary === false) {
				return;
			}

			this.onPointerDownMouseX = event.clientX;
			this.onPointerDownMouseY = event.clientY;

			this.onPointerDownLon = this.lon;
			this.onPointerDownLat = this.lat;

			// It attach the Pointer Move and Pointer Up Events
			document.addEventListener('pointermove', this.onPointerMove);
			document.addEventListener('pointerup', this.onPointerUp);
		}
	}

	/**
	 * It attach the Pointer Move Event
	 * @param event
	 */
	public onPointerMove = (event: any): void => {
		if (event.type === 'pointermove') {
			event.cancelBubble = true;
			event.returnValue = false;
		}

		if (this.reportImage.disabledControls === false && !this.isComponentAlreadyDispose()) {
			if (event.isPrimary === false) {
				return;
			}

			this.lon = (this.onPointerDownMouseX - event.clientX) * 0.1 + this.onPointerDownLon;
			this.lat = (event.clientY - this.onPointerDownMouseY) * 0.1 + this.onPointerDownLat;
		}
	}

	/**
	 * It attach the Pointer Up Event
	 * @param event
	 */
	public onPointerUp = (event: any): void => {
		if (this.reportImage.disabledControls === false && !this.isComponentAlreadyDispose()) {
			if (event['isPrimary'] === false) {
				return;
			}
			document.removeEventListener('pointermove', this.onPointerMove);
			document.removeEventListener('pointerup', this.onPointerUp);
		}
	}

	/**
	 * We Execute the animation
	 */
	public animate = (): void => {
		if (!this.isComponentAlreadyDispose()) {
			this.currentAnimation = requestAnimationFrame(this.animate);
			this.update();
		}
	}

	/**
	 * Re-render the scene continuously
	 */
	public update = (): void => {
		if (this.reportImage.disabledControls === false && !this.isComponentAlreadyDispose()) {
			this.lat = Math.max(-85, Math.min(85, this.lat));
			this.phi = THREE.MathUtils.degToRad(90 - this.lat);
			this.theta = THREE.MathUtils.degToRad(this.lon);

			this.povPosition.x = 500 * Math.sin(this.phi) * Math.cos(this.theta);
			this.povPosition.y = 500 * Math.cos(this.phi);
			this.povPosition.z = 500 * Math.sin(this.phi) * Math.sin(this.theta);

			this.sceneRender.camera.lookAt(this.povPosition.x, this.povPosition.y, this.povPosition.z);

			this.sceneRender.renderer.render(this.sceneRender.scene, this.sceneRender.camera);
		}
	}

	/**
	 * Create the Snapshot to create an SmartTag or a Report Image
	 */
	public createSnapshot(captureType: CaptureType): any {

		if (this.reportImage.disabledControls === false && !this.isComponentAlreadyDispose()) {
			this.reportImage.snapshot = null;
			this.reportImage.disabledControls = true;
			this.reportImage.successUpload = false;
			this.reportImage.captureType = captureType;

			let mediaCreationType = 'ReportImage';
			if (captureType === CaptureType.SMARTTAG) {
				mediaCreationType = 'SmartTag';
			}
			this.broadcastService.broadcast({
				name: FORM_EVENT.TELEMETRY,
				event: {
					telemetryEventName: `3dTour-${mediaCreationType}-START`,
					data: {
						path: this.data._Path
					}
				}
			});

			this.createFakeSceneRender();
		}
	}

	/**
	 * Disable any Scroll Interaction
	 */
	public disableScroll(): void {
		const elements = document.getElementsByClassName('json-form-wrapper');
		elements[0].addEventListener('wheel', this.preventDefault, { passive: false });
	}

	/**
	 * Enable any Scroll Interaction
	 */
	public enableScroll(): void {
		const elements = document.getElementsByClassName('json-form-wrapper');
		elements[0].removeEventListener('wheel', this.preventDefault, false);
	}

	/**
	 * Remove event listeners
	 */
	ngOnDestroy(): void {

		this.layoutRenderService.componentBroadcast.next({
			eventType: RMCEventType.CLOSE,
			jsonPath: this.thisData._Path,
		});

		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();

		document.removeEventListener('wheel', this.onMouseWheel);
		document.removeEventListener('dragover', this.onDragOver);
		this.dispose();
	}

	/**
	 * Create a Fake Render on Fly to have the best quality
	 * @private
	 */
	private createFakeSceneRender(): void {

		const fakeSceneRenderContainer = this.elementRef.nativeElement.querySelector('.fake-scene-render');

		const perspective = {
			width: 3000,
			height: 2250
		};

		this.fakeSceneRender.camera = new THREE.PerspectiveCamera(75, perspective.width / perspective.height, 1, 1100);

		const imageTexture = new THREE.MeshBasicMaterial({ map: this.currentImageTexture });

		this.fakeSceneRender.mesh = new THREE.Mesh(this.geometry, imageTexture);

		this.fakeSceneRender.scene = new THREE.Scene();
		this.fakeSceneRender.scene.add(this.fakeSceneRender.mesh);

		this.fakeSceneRender.renderer = new THREE.WebGLRenderer();
		this.fakeSceneRender.renderer.setPixelRatio(1);
		this.fakeSceneRender.renderer.setSize(perspective.width, perspective.height);

		fakeSceneRenderContainer.appendChild(this.fakeSceneRender.renderer.domElement);

		this.fakeSceneRender.camera.lookAt(this.povPosition.x, this.povPosition.y, this.povPosition.z);
		this.fakeSceneRender.camera.fov = THREE.MathUtils.clamp(this.povPosition.fov, 10, 95);
		this.fakeSceneRender.camera.updateProjectionMatrix();

		this.fakeSceneRender.renderer.render(this.fakeSceneRender.scene, this.fakeSceneRender.camera);

		// Create and Keep in Memory the Image
		// Get the current Canvas
		const strMime = 'image/jpeg';
		this.reportImage.snapshot = this.fakeSceneRender.renderer.domElement.toDataURL(strMime, 1);

		// Clear previous Scene
		fakeSceneRenderContainer.innerHTML = '';

		//
		this.openResourceMediaControlPanel();
	}

	/**
	 * Prepare and Open the Media Control Panel
	 */
	public openResourceMediaControlPanel(): void {
		const resourceMediaModel: ResourceMediaModel = new ResourceMediaModel();

		resourceMediaModel.snapshot = {
			preview: true,
			blob: this.reportImage.snapshot,
			renderer: this.fakeSceneRender.renderer,
			metadata: this.currentAlbumPanorama.metadata
		};
		resourceMediaModel.captureType = this.reportImage.captureType;
		resourceMediaModel.formModelType = FormModelType.CREATE;

		// Open the Resource Media Control Panel
		this.layoutRenderService.componentBroadcast.next({
			eventType: RMCEventType.OPEN,
			jsonPath: this.thisData._Path,
			metaData: {
				resourceMediaModel: resourceMediaModel
			},
			component: ComponentList.PANORAMA_TOUR
		});
	}

	/**
	 * Required to create a signature for the RemoveListener only
	 * @param e
	 * @private
	 */
	private preventDefault(e: any): void {
		e.preventDefault();
	}


	/**
	 * Event to open modal to draw forms in images for smart tags
	 * @private
	 */
	openBlurModal(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: DrawFormsComponent,
				data: {
					jsonPath: this.thisData._Path,
					currentAlbumImage: this.album[0],
					photos: this.photos,
					order: this.viewsService.order
				},
				modalConfiguration: {
					modalCustomClass: 'blur-modal',
					title: `Apply Blur`,
					modalSize: ModalSize.XXL,
					titleIcon: 'far fa-pen-paintbrush'
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (result) => {
				if (result.id) {
					const photoSelectedIndex = this.photos.findIndex(item => item.id === result.id);

					const albumSelectedIndex = this.album.findIndex(item => item.id === result.id);

					if (photoSelectedIndex >= 0) {
						const newPhotoAlbum = ObjectUtils.recursiveDeepCopy(this.photos[photoSelectedIndex])

						newPhotoAlbum.path = result.path;
						newPhotoAlbum.thumbnails = result.thumbnails;
						newPhotoAlbum['forceReload'] = Date.now();
						this.album.splice(albumSelectedIndex, 1, newPhotoAlbum);
						this.photos.splice(photoSelectedIndex, 1, newPhotoAlbum);
						this.dispose();
						this.initializePanoramaTour();
					}

				}
			});
	}

}
